import { useCallback, useContext, useMemo, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { CollectionDetailsContext, Config } from ".";
import { captureDB } from "../../lib/indexed-db";
import { useLocation } from "react-router-dom";
import { apiGetUserProfile } from "../../lib/api";
import {
  getAdditionalDataRequiredFields,
  getPrismicTaskInstructions,
} from "../../lib/prismic";
import { useState } from "react";
import { LabelRelation } from "types";
import { getCanonicalLabelValue, getLabelProposals } from "lib/label-relations";
import { getCurrentSessionId } from "lib/session";

export interface CollectionDetailsFormValue {
  device: string;
  camera_not_flipped: boolean;
  labels: Record<string, string | Array<string>>;
  data: Array<string>;
  task?: string;
}

// Helper function to check if a data entry is additional data (from user profile)
const isAdditionalData = (item: string): boolean => {
  // Check if the item follows the pattern "key: value" where key is a profile field
  // This prevents profile data from being displayed as editable fields in the form

  const profileFields = ["height", "weight", "eye color"]; // Profile fields from user data
  return profileFields.some((field) => item.startsWith(`${field}:`));
};

export function useCollectionDetailsForm(props?: {
  onSubmit?: (data: CollectionDetailsFormValue) => void;
  labelsOverride?: Config["labels"];
  labelRelationsOverride?: LabelRelation[];
  additionalDataRequiredOverride?: string[];
  knownValuesRefreshKey?: unknown;
  schemaFingerprint?: string;
}) {
  const { config, contributor } = useContext(CollectionDetailsContext);
  const labels = props?.labelsOverride ?? config?.labels;
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [proposalError, setProposalError] = useState("");
  const [knownLabelValues, setKnownLabelValues] = useState<
    Record<string, string>
  >({});

  const taskId = location.state?.taskId;

  const [collectionDetailsFromDB, setCollectionDetailsFromDB] =
    useState<any>(undefined);

  // Load collection details directly from IndexedDB step 0 when taskId changes
  useEffect(() => {
    if (!taskId) return;

    const loadFromIndexedDB = async () => {
      try {
        const storedCaptures = await captureDB.getStepCaptures(taskId);
        const step0 = storedCaptures.find((s) => s.stepNumber === 0);

        if (step0?.collectionDetails) {
          // Filter out additional data from the stored data array
          const filtered = { ...step0.collectionDetails };
          if (filtered.data && Array.isArray(filtered.data)) {
            filtered.data = filtered.data.filter(
              (item: string) => !isAdditionalData(item)
            );
          }

          setCollectionDetailsFromDB(filtered);
        } else {
          setCollectionDetailsFromDB(null);
        }
      } catch (error) {
        console.error(
          `Error loading collection details for task ${taskId}:`,
          error
        );
        setCollectionDetailsFromDB(null);
      }
    };

    loadFromIndexedDB();
  }, [taskId]);

  const defaultValues = useMemo(() => {
    // Always return a complete form structure to ensure all fields are reset
    const result = {
      data: [],
      task: taskId,
      device: "",
      camera_not_flipped: false,
      labels: {},
      ...(collectionDetailsFromDB || {}), // Apply IndexedDB values if any
    };
    return result;
  }, [taskId, collectionDetailsFromDB]); // Re-calculate when taskId or DB data changes

  const formState = useForm<CollectionDetailsFormValue>({ defaultValues });
  const watchedLabelValues = useWatch({
    control: formState.control,
    name: "labels",
  });
  const watchedLabels = useMemo(
    () => watchedLabelValues || {},
    [watchedLabelValues]
  );
  const labelRelations = useMemo(
    () => props?.labelRelationsOverride ?? config?.labelRelations ?? [],
    [config?.labelRelations, props?.labelRelationsOverride]
  );

  // Reset form when defaultValues change (loaded from IndexedDB)
  useEffect(() => {
    formState.reset(defaultValues);
  }, [defaultValues, formState, taskId]);

  useEffect(() => {
    if (!taskId || !labels || collectionDetailsFromDB === undefined) {
      return;
    }

    let cancelled = false;
    const loadKnownValues = async () => {
      let userValues: Record<string, string> = {};
      let sessionValues: Record<string, string> = {};

      try {
        const profile = await apiGetUserProfile();
        userValues = Object.fromEntries(
          Object.entries(profile)
            .filter(
              ([, value]) =>
                typeof value === "string" || typeof value === "number"
            )
            .map(([id, value]) => [id, String(value)])
        );
      } catch (error) {
        console.error("Could not load user label proposals:", error);
      }

      try {
        const sessionId = await getCurrentSessionId(contributor);
        if (sessionId)
          sessionValues = await captureDB.getSessionLabels(sessionId);
      } catch (error) {
        console.error("Could not load session label proposals:", error);
      }

      if (!cancelled) {
        setKnownLabelValues({ ...sessionValues, ...userValues });
      }
    };

    loadKnownValues();
    return () => {
      cancelled = true;
    };
  }, [
    collectionDetailsFromDB,
    contributor,
    labels,
    props?.knownValuesRefreshKey,
    taskId,
  ]);

  useEffect(() => {
    if (!taskId || !labels || collectionDetailsFromDB === undefined) return;

    setProposalError("");
    try {
      const currentValues = Object.fromEntries(
        Object.entries(watchedLabels).flatMap(([id, value]) =>
          typeof value === "string" && value.trim() ? [[id, value]] : []
        )
      );
      const taskValues = Object.fromEntries(
        Object.entries(collectionDetailsFromDB?.labels || {}).flatMap(
          ([id, value]) =>
            typeof value === "string" && value.trim() ? [[id, value]] : []
        )
      );
      const targets = Object.entries(labels).flatMap(([id, label]) => {
        if (Array.isArray(label) || label.field_type !== "dropdown") return [];
        return [
          {
            id,
            allowedValues: Object.values(label.labels),
            options: Object.entries(label.labels).map(([key, value]) => ({
              key,
              value,
            })),
            relatedLabelId: label.related_to,
          },
        ];
      });
      const proposals = getLabelProposals({
        relations: labelRelations,
        knownValues: {
          ...knownLabelValues,
          ...taskValues,
          ...currentValues,
        },
        targets,
      });
      Object.entries(proposals).forEach(([id, value]) => {
        if (formState.getFieldState(`labels.${id}`).isTouched) return;
        const current = formState.getValues(`labels.${id}`);
        if (!current || current.length === 0) {
          formState.setValue(`labels.${id}`, value);
        }
      });
    } catch (error) {
      console.error("Invalid Prismic label proposal configuration:", error);
      setProposalError(
        "This task has an invalid label proposal configuration."
      );
    }
  }, [
    collectionDetailsFromDB,
    formState,
    knownLabelValues,
    labelRelations,
    labels,
    taskId,
    watchedLabels,
  ]);

  const addManualLabel = useCallback(() => {
    formState.setValue("data", [...(formState.getValues("data") || []), ""], {
      shouldValidate: true,
    });
  }, [formState]);

  const submit = useCallback(async () => {
    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      formState.clearErrors();
      let isError = false;
      const formValue = formState.getValues();

      if (labels?.devices && labels.devices.length > 0 && !formValue.device) {
        formState.setError("device", { message: "Device is required" });
        isError = true;
      }

      // Only validate task-level fields (those with field_type)
      Object.entries(labels || {}).forEach(([id, label]) => {
        const labelValue = formValue.labels?.[id];
        if ("required" in label && label.required && label.field_type) {
          // Check validation for task-level input fields
          let isEmpty = false;

          if (["text", "numeric", "date"].includes(label.field_type)) {
            // For input fields, check if value is empty string or undefined
            isEmpty =
              !labelValue ||
              (typeof labelValue === "string" && labelValue.trim() === "");
          } else if (label.field_type === "dropdown") {
            // For dropdown fields, check if value is empty
            isEmpty = !labelValue || labelValue.length <= 0;
          }

          if (isEmpty) {
            formState.setError(`labels.${id}`, {
              message: `${label.title || "Field"} is required`,
            });
            isError = true;
          }
        }
      });

      if (isError) {
        setIsSubmitting(false);
        return;
      }

      // Get user's additional data (height, weight, etc.)
      let userAdditionalData: Record<string, any> = {};
      let requiredFields: string[] = [];

      try {
        // If we have a taskId, fetch the required fields for this task
        if (taskId) {
          const taskInstructions = await getPrismicTaskInstructions(
            taskId,
            config?.selectedCollection
          );
          requiredFields =
            props?.additionalDataRequiredOverride ??
            getAdditionalDataRequiredFields(taskInstructions);
        }

        userAdditionalData = await apiGetUserProfile();
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }

      // Include only the required additional data fields for this task
      const additionalDataEntries =
        requiredFields.length > 0
          ? requiredFields
              .filter(
                (field) =>
                  userAdditionalData[field] !== undefined &&
                  userAdditionalData[field] !== null &&
                  userAdditionalData[field] !== ""
              )
              .map((field) => `${field}: ${userAdditionalData[field]}`)
          : [];

      // Combine manual data (from form) with additional data (from profile)
      const combinedData = [
        ...formValue.data.filter((item) => item.length > 0),
        ...additionalDataEntries,
      ];

      const configuredLabels = props?.labelsOverride
        ? Object.fromEntries(
            Object.entries(labels || {})
              .filter(([, label]) => !Array.isArray(label))
              .map(([id]) => [id, formValue.labels?.[id]])
              .filter(([, value]) => value !== undefined)
          )
        : formValue.labels;

      const result = {
        device: formValue.device,
        labels: configuredLabels,
        data: combinedData,
        camera_not_flipped: formValue.camera_not_flipped,
        task: taskId,
        collection_id: config?.collection_id || "",
      };

      // Save to IndexedDB as step 0 for task isolation
      if (taskId) {
        try {
          await captureDB.saveStepCapture(taskId, {
            stepNumber: 0,
            stepLabel: "Collection Details",
            stepTitle: "Task Information",
            numberOfCaptures: 0,
            schemaFingerprint: props?.schemaFingerprint,
            files: [],
            collectionDetails: result,
          });

          const sessionLabelValues = Object.fromEntries(
            Object.entries(labels || {}).flatMap(([id, label]) => {
              const value = formValue.labels?.[id];
              if (
                Array.isArray(label) ||
                typeof value !== "string" ||
                !value.trim()
              ) {
                return [];
              }
              const options = Object.entries(label.labels).map(
                ([key, optionValue]) => ({ key, value: optionValue })
              );
              return [[id, getCanonicalLabelValue(options, value)]];
            })
          );
          if (Object.keys(sessionLabelValues).length > 0) {
            try {
              const sessionId = await getCurrentSessionId(contributor);
              if (sessionId) {
                await captureDB.saveSessionLabels(
                  sessionId,
                  sessionLabelValues
                );
              } else {
                console.warn(
                  "Session labels could not be retained because no session ID is available"
                );
              }
            } catch (error) {
              console.error("Failed to retain session labels:", error);
            }
          }

          // Update local state to reflect saved data
          setCollectionDetailsFromDB(result);
        } catch (error) {
          console.error(
            "Failed to save collection details to IndexedDB:",
            error
          );
          throw error;
        }
      } else {
        console.error("No taskId provided - cannot save collection details");
        throw new Error("Task ID is required to save collection details");
      }

      props?.onSubmit?.(result);
    } catch (error) {
      console.error("Error in submit:", error);
      throw error;
    } finally {
      // Reset submitting state after a delay to ensure state updates complete
      setTimeout(() => {
        setIsSubmitting(false);
      }, 100);
    }
  }, [formState, config, contributor, props, taskId, isSubmitting, labels]);

  return {
    formState,
    addManualLabel,
    labels,
    submit,
    isSubmitting,
    proposalError,
  };
}
